"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { DynamicDocumentInstructions, WorkflowAction } from "@/configuration/document-mappings/types";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { useDocumentManagementInstructions } from "@/src/lib/data-fetchers/document-management-instructions-fetchers";
import { useFaxLocationAssignment } from "@/src/lib/data-fetchers/location-assignment-fetcher";
import { isPassthroughAssignee } from "@/src/lib/ehr/customer/heartplace/passthrough-assignee";
import { tryGetEhrSystemId } from "@/src/lib/resolve-ehr-ids";
import { renderTemplate } from "@/src/lib/templating";
import type { StepOutputs } from "@/src/lib/workflows/steps/step-outputs";
import { Classification } from "@/src/models/constants/classifications";
import { Section } from "@/src/models/section";
import { Task } from "@/src/models/task";

// Groups whose staff location comes from a live eCW encounter lookup rather than static fax-number config.
const GROUPS_RESOLVING_FACILITY_ASYNC: ReadonlySet<string> = new Set([ConfigSubdomain.PA_CENTRAL_REGION, ConfigSubdomain.PA_EAST_REGION] satisfies readonly ConfigSubdomain[]);
export interface InstructionField {
  label: string;
  value: string;
}
export interface ProcessedInstructions {
  displayName: string;
  standardFields: InstructionField[];
  extraFields: InstructionField[];
  instructions: readonly string[] | null;
  note: string | null;
  noteLabel: string | null;
  statusLine: string | null;
  workflowAction: WorkflowAction | null;
  additionalWorkflow: readonly string[] | null;
  faxRecipientNumber: string | null;
  tags: readonly string[] | null;
  stepOutputs: StepOutputs | null;
  ccDescription: string | null;
  processInstructionText: (text: string) => string;
}
interface LocationAssignment {
  authLocation?: string | null;
  staffLocation?: string | null;
  locationPrefix?: string | null;
  faxRecipientNumber?: string | null;
  resolvedAssignment?: string | null;
  isLoading?: boolean;
}
function getGroupExtraFields(groupName: string, patientFamilyName: string | null | undefined, patientGivenName: string | null | undefined, patientEhrSystemId: string | null, ehrDocumentId: string | null | undefined): InstructionField[] {
  switch (groupName) {
    case ConfigSubdomain.STRIDECARE:
      return [{
        label: "Patient Name",
        value: [patientFamilyName, patientGivenName].filter(Boolean).join(", ") || "N/A"
      }, {
        label: "EHR ID",
        value: patientEhrSystemId || "N/A"
      }, {
        label: "Document ID",
        value: ehrDocumentId || "N/A"
      }];
    default:
      return [];
  }
}
function replaceLocationPlaceholder(text: string | null, locationValue: string | null): string {
  if (!text) {
    return locationValue || "";
  }
  if (!locationValue) {
    return text;
  }
  return text.replace(/\{\{?location\}\}?/g, locationValue);
}
export function useBuildInstructions(task, section, mapping, stepOutputs, ccDescription, t0) {
  const $ = _c(83);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const location = t1;
  const {
    authLocation: t2,
    staffLocation: t3,
    locationPrefix: t4,
    faxRecipientNumber: t5,
    resolvedAssignment: t6,
    isLoading: t7
  } = location;
  const authLocation = t2 === undefined ? null : t2;
  const staffLocation = t3 === undefined ? null : t3;
  const locationPrefix = t4 === undefined ? null : t4;
  const faxRecipientNumber = t5 === undefined ? null : t5;
  const resolvedAssignment = t6 === undefined ? null : t6;
  const isLocationLoading = t7 === undefined ? false : t7;
  const reviewedContent = section.reviewedContent;
  const patientFamilyName = task.patient?.familyName;
  const patientGivenName = task.patient?.givenName;
  let t8;
  if ($[2] !== task.patient) {
    t8 = task.patient ? tryGetEhrSystemId(task.patient) : null;
    $[2] = task.patient;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  const patientEhrSystemId = t8;
  const ehrDocumentId = section.ehrDocumentId;
  let t9;
  bb0: {
    if (!mapping) {
      t9 = null;
      break bb0;
    }
    let isHeartplaceNonMrr;
    let isPaReferralRequestFacility;
    let processInstructionText;
    let t10;
    let t11;
    let t12;
    let t13;
    let t14;
    let t15;
    let t16;
    if ($[4] !== authLocation || $[5] !== mapping.classification || $[6] !== mapping.displayName || $[7] !== mapping.documentLabel || $[8] !== mapping.folder || $[9] !== mapping.group || $[10] !== mapping.inputDate || $[11] !== mapping.priority || $[12] !== mapping.workflowType || $[13] !== reviewedContent) {
      const templateContent = reviewedContent || {};
      const group = {
        subdomain: mapping.group
      };
      processInstructionText = text => {
        const rendered = renderTemplate(text, templateContent, group);
        return replaceLocationPlaceholder(rendered, authLocation);
      };
      isHeartplaceNonMrr = mapping.group === ConfigSubdomain.USHV_HEARTPLACE;
      isPaReferralRequestFacility = (mapping.group === ConfigSubdomain.PA_EAST_REGION || mapping.group === ConfigSubdomain.PA_CENTRAL_REGION) && mapping.classification === Classification.REFERRAL_REQUEST;
      t12 = !isHeartplaceNonMrr ? [{
        label: "Workflow Type",
        value: renderTemplate(mapping.workflowType, templateContent, group)
      }] : [];
      t13 = {
        label: "Priority",
        value: renderTemplate(mapping.priority, templateContent, group)
      };
      if ($[24] !== mapping.displayName) {
        t14 = {
          label: "Document type",
          value: mapping.displayName
        };
        $[24] = mapping.displayName;
        $[25] = t14;
      } else {
        t14 = $[25];
      }
      t15 = {
        label: "Document label",
        value: renderTemplate(mapping.documentLabel, templateContent, group)
      };
      if ($[26] !== mapping.inputDate) {
        t16 = {
          label: "Input date",
          value: mapping.inputDate
        };
        $[26] = mapping.inputDate;
        $[27] = t16;
      } else {
        t16 = $[27];
      }
      t10 = isHeartplaceNonMrr ? "Folder" : "Upload to folder";
      t11 = renderTemplate(mapping.folder || "", templateContent, group);
      $[4] = authLocation;
      $[5] = mapping.classification;
      $[6] = mapping.displayName;
      $[7] = mapping.documentLabel;
      $[8] = mapping.folder;
      $[9] = mapping.group;
      $[10] = mapping.inputDate;
      $[11] = mapping.priority;
      $[12] = mapping.workflowType;
      $[13] = reviewedContent;
      $[14] = isHeartplaceNonMrr;
      $[15] = isPaReferralRequestFacility;
      $[16] = processInstructionText;
      $[17] = t10;
      $[18] = t11;
      $[19] = t12;
      $[20] = t13;
      $[21] = t14;
      $[22] = t15;
      $[23] = t16;
    } else {
      isHeartplaceNonMrr = $[14];
      isPaReferralRequestFacility = $[15];
      processInstructionText = $[16];
      t10 = $[17];
      t11 = $[18];
      t12 = $[19];
      t13 = $[20];
      t14 = $[21];
      t15 = $[22];
      t16 = $[23];
    }
    let t17;
    if ($[28] !== t10 || $[29] !== t11) {
      t17 = {
        label: t10,
        value: t11
      };
      $[28] = t10;
      $[29] = t11;
      $[30] = t17;
    } else {
      t17 = $[30];
    }
    let t18;
    if ($[31] !== authLocation || $[32] !== locationPrefix || $[33] !== mapping.assignedTo || $[34] !== resolvedAssignment) {
      t18 = resolvedAssignment ?? (authLocation && isPassthroughAssignee(authLocation) && /\{\{?location\}\}?/.test(mapping.assignedTo ?? "") ? authLocation : replaceLocationPlaceholder(mapping.assignedTo?.replace(/\{\{?authPrefix\}\}?/g, locationPrefix ?? "HP") ?? null, authLocation));
      $[31] = authLocation;
      $[32] = locationPrefix;
      $[33] = mapping.assignedTo;
      $[34] = resolvedAssignment;
      $[35] = t18;
    } else {
      t18 = $[35];
    }
    let t19;
    if ($[36] !== t18) {
      t19 = {
        label: "Assign to",
        value: t18
      };
      $[36] = t18;
      $[37] = t19;
    } else {
      t19 = $[37];
    }
    let standardFields;
    if ($[38] !== mapping.markAsReviewed || $[39] !== t12 || $[40] !== t13 || $[41] !== t14 || $[42] !== t15 || $[43] !== t16 || $[44] !== t17 || $[45] !== t19) {
      standardFields = [...t12, t13, t14, t15, t16, t17, t19].filter(_temp);
      if (mapping.markAsReviewed != null) {
        const t20 = mapping.markAsReviewed ? "Yes" : "No";
        let t21;
        if ($[47] !== t20) {
          t21 = {
            label: "Mark as reviewed",
            value: t20
          };
          $[47] = t20;
          $[48] = t21;
        } else {
          t21 = $[48];
        }
        standardFields.push(t21);
      }
      $[38] = mapping.markAsReviewed;
      $[39] = t12;
      $[40] = t13;
      $[41] = t14;
      $[42] = t15;
      $[43] = t16;
      $[44] = t17;
      $[45] = t19;
      $[46] = standardFields;
    } else {
      standardFields = $[46];
    }
    let extraFields;
    if ($[49] !== ehrDocumentId || $[50] !== isHeartplaceNonMrr || $[51] !== isLocationLoading || $[52] !== isPaReferralRequestFacility || $[53] !== locationPrefix || $[54] !== mapping.group || $[55] !== mapping.teMessage || $[56] !== patientEhrSystemId || $[57] !== patientFamilyName || $[58] !== patientGivenName || $[59] !== staffLocation) {
      extraFields = [...getGroupExtraFields(mapping.group, patientFamilyName, patientGivenName, patientEhrSystemId, ehrDocumentId)];
      if (staffLocation) {
        const staffLocationValue = isHeartplaceNonMrr && locationPrefix ? `${locationPrefix} ${staffLocation}` : staffLocation;
        const t20 = isHeartplaceNonMrr ? "Fax sent to" : isPaReferralRequestFacility ? "Facility" : "Staff Location";
        let t21;
        if ($[61] !== staffLocationValue || $[62] !== t20) {
          t21 = {
            label: t20,
            value: staffLocationValue
          };
          $[61] = staffLocationValue;
          $[62] = t20;
          $[63] = t21;
        } else {
          t21 = $[63];
        }
        extraFields.push(t21);
      } else {
        if (isLocationLoading && GROUPS_RESOLVING_FACILITY_ASYNC.has(mapping.group)) {
          const t20 = isPaReferralRequestFacility ? "Facility" : "Staff Location";
          let t21;
          if ($[64] !== t20) {
            t21 = {
              label: t20,
              value: "Loading\u2026"
            };
            $[64] = t20;
            $[65] = t21;
          } else {
            t21 = $[65];
          }
          extraFields.push(t21);
        }
      }
      if (mapping.teMessage) {
        let t20;
        if ($[66] !== mapping.teMessage) {
          t20 = {
            label: "TE message",
            value: mapping.teMessage
          };
          $[66] = mapping.teMessage;
          $[67] = t20;
        } else {
          t20 = $[67];
        }
        extraFields.push(t20);
      }
      $[49] = ehrDocumentId;
      $[50] = isHeartplaceNonMrr;
      $[51] = isLocationLoading;
      $[52] = isPaReferralRequestFacility;
      $[53] = locationPrefix;
      $[54] = mapping.group;
      $[55] = mapping.teMessage;
      $[56] = patientEhrSystemId;
      $[57] = patientFamilyName;
      $[58] = patientGivenName;
      $[59] = staffLocation;
      $[60] = extraFields;
    } else {
      extraFields = $[60];
    }
    const t20 = mapping.note ?? null;
    const t21 = mapping.noteLabel ?? null;
    const t22 = mapping.statusLine ?? null;
    const t23 = mapping.workflowAction ?? null;
    const t24 = mapping.tags ?? null;
    const t25 = stepOutputs ?? null;
    const t26 = ccDescription ?? null;
    let t27;
    if ($[68] !== extraFields || $[69] !== faxRecipientNumber || $[70] !== mapping.additionalWorkflow || $[71] !== mapping.displayName || $[72] !== mapping.instructions || $[73] !== processInstructionText || $[74] !== standardFields || $[75] !== t20 || $[76] !== t21 || $[77] !== t22 || $[78] !== t23 || $[79] !== t24 || $[80] !== t25 || $[81] !== t26) {
      t27 = {
        displayName: mapping.displayName,
        standardFields,
        extraFields,
        instructions: mapping.instructions,
        note: t20,
        noteLabel: t21,
        statusLine: t22,
        workflowAction: t23,
        additionalWorkflow: mapping.additionalWorkflow,
        faxRecipientNumber,
        tags: t24,
        stepOutputs: t25,
        ccDescription: t26,
        processInstructionText
      };
      $[68] = extraFields;
      $[69] = faxRecipientNumber;
      $[70] = mapping.additionalWorkflow;
      $[71] = mapping.displayName;
      $[72] = mapping.instructions;
      $[73] = processInstructionText;
      $[74] = standardFields;
      $[75] = t20;
      $[76] = t21;
      $[77] = t22;
      $[78] = t23;
      $[79] = t24;
      $[80] = t25;
      $[81] = t26;
      $[82] = t27;
    } else {
      t27 = $[82];
    }
    t9 = t27;
  }
  return t9;
}
function _temp(field) {
  return field.value;
}
export function useProcessedInstructions(task, section, t0) {
  const $ = _c(17);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] !== enabled) {
    t1 = {
      enabled
    };
    $[0] = enabled;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    instructions,
    stepOutputs,
    ccDescription,
    isLoading: docLoading,
    isError: docError
  } = useDocumentManagementInstructions(task.id, t1);
  let t2;
  if ($[2] !== enabled) {
    t2 = {
      enabled
    };
    $[2] = enabled;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    authLocation,
    staffLocation,
    faxRecipientNumber,
    locationPrefix,
    resolvedAssignment,
    isLoading: locLoading
  } = useFaxLocationAssignment(task.id, t2);
  let t3;
  if ($[4] !== instructions) {
    t3 = instructions ? GROUPS_RESOLVING_FACILITY_ASYNC.has(instructions.group) : false;
    $[4] = instructions;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const resolvesFacilityAsync = t3;
  const t4 = resolvesFacilityAsync && locLoading;
  let t5;
  if ($[6] !== authLocation || $[7] !== faxRecipientNumber || $[8] !== locationPrefix || $[9] !== resolvedAssignment || $[10] !== staffLocation || $[11] !== t4) {
    t5 = {
      authLocation,
      staffLocation,
      locationPrefix,
      faxRecipientNumber,
      resolvedAssignment,
      isLoading: t4
    };
    $[6] = authLocation;
    $[7] = faxRecipientNumber;
    $[8] = locationPrefix;
    $[9] = resolvedAssignment;
    $[10] = staffLocation;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const data = useBuildInstructions(task, section, instructions, stepOutputs, ccDescription, t5);
  const t6 = docLoading || !resolvesFacilityAsync && locLoading;
  let t7;
  if ($[13] !== data || $[14] !== docError || $[15] !== t6) {
    t7 = {
      isLoading: t6,
      isError: docError,
      data
    };
    $[13] = data;
    $[14] = docError;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}